import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../lib/api';

export default function Dashboard() {
  const navigate = useNavigate();

  const { data: authData } = useQuery({
    queryKey: ['auth'],
    queryFn: api.auth.me,
  });

  const { data: instancesData } = useQuery({
    queryKey: ['instances'],
    queryFn: api.instances.list,
  });

  const user = authData?.user;
  const instances = instancesData?.instances || [];

  const getTypeColor = (type) => {
    switch (type) {
      case 'BOARD':
        return 'bg-sonarges-red-100 text-sonarges-red-700 dark:bg-sonarges-red-900/30 dark:text-sonarges-red-400';
      case 'COMMITTEE':
        return 'bg-sonarges-green-100 text-sonarges-green-700 dark:bg-sonarges-green-900/30 dark:text-sonarges-green-400';
      case 'PROJECT':
        return 'bg-sonarges-gray-100 text-sonarges-gray-700 dark:bg-sonarges-gray-700 dark:text-sonarges-gray-300';
      default:
        return 'bg-sonarges-gray-100 text-sonarges-gray-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'BOARD':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'COMMITTEE':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'PROJECT':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-sonarges-gray-900 dark:text-white">
              Tableau de bord
            </h1>
            <p className="mt-2 text-sonarges-gray-600 dark:text-sonarges-gray-400">
              Bienvenue, {user?.name}
            </p>
          </div>
          <button
            onClick={() => navigate('/instances')}
            className="px-6 py-3 bg-gradient-to-r from-sonarges-green-600 to-sonarges-green-500 text-white font-semibold rounded-lg hover:from-sonarges-green-700 hover:to-sonarges-green-600 transform transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Gérer les instances</span>
          </button>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-sonarges-gray-900 dark:text-white mb-6">
            Mes Instances
          </h2>
          
          {instances.length === 0 ? (
            <div className="bg-white dark:bg-sonarges-gray-800 rounded-xl shadow-lg p-12 text-center border-2 border-dashed border-sonarges-gray-300 dark:border-sonarges-gray-600">
              <svg className="w-16 h-16 mx-auto text-sonarges-gray-400 dark:text-sonarges-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="text-lg font-semibold text-sonarges-gray-900 dark:text-white mb-2">
                Aucune instance disponible
              </h3>
              <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400 mb-6">
                Vous n'êtes membre d'aucune instance pour le moment
              </p>
              <button
                onClick={() => navigate('/instances')}
                className="inline-flex items-center px-4 py-2 bg-sonarges-green-600 text-white font-medium rounded-lg hover:bg-sonarges-green-700 transition-colors"
              >
                Créer une instance
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {instances.map((instance) => (
                <div
                  key={instance.id}
                  onClick={() => navigate(`/instances/${instance.id}`)}
                  className="group bg-white dark:bg-sonarges-gray-800 rounded-xl shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-sonarges-green-500 dark:hover:border-sonarges-green-600 transform hover:-translate-y-1"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-lg ${getTypeColor(instance.type)}`}>
                        {getTypeIcon(instance.type)}
                      </div>
                      <span className={`px-3 py-1 text-xs font-bold rounded-full ${getTypeColor(instance.type)}`}>
                        {instance.type}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-sonarges-gray-900 dark:text-white mb-2 group-hover:text-sonarges-green-600 dark:group-hover:text-sonarges-green-400 transition-colors">
                      {instance.name}
                    </h3>
                    
                    <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400 text-sm line-clamp-2 mb-4">
                      {instance.description || 'Aucune description disponible'}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-sonarges-gray-500 dark:text-sonarges-gray-400">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        Membres
                      </span>
                      <span className="text-sonarges-green-600 dark:text-sonarges-green-400 font-semibold group-hover:underline">
                        Voir détails →
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-xl font-semibold text-sonarges-gray-900 dark:text-white mb-6">
            Navigation rapide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-sonarges-green-50 to-white dark:from-sonarges-green-900/20 dark:to-sonarges-gray-800 p-6 rounded-xl shadow-lg border border-sonarges-green-200 dark:border-sonarges-green-800 hover:shadow-xl transition-all duration-200">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-sonarges-green-100 dark:bg-sonarges-green-900/30 rounded-lg">
                  <svg className="w-6 h-6 text-sonarges-green-600 dark:text-sonarges-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <h3 className="font-bold text-sonarges-gray-900 dark:text-white mb-2">Réunions à venir</h3>
              <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400 text-sm">
                Consultez vos prochaines réunions planifiées
              </p>
            </div>

            <div className="bg-gradient-to-br from-sonarges-gray-50 to-white dark:from-sonarges-gray-800 dark:to-sonarges-gray-800 p-6 rounded-xl shadow-lg border border-sonarges-gray-200 dark:border-sonarges-gray-700 hover:shadow-xl transition-all duration-200">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-sonarges-gray-100 dark:bg-sonarges-gray-700 rounded-lg">
                  <svg className="w-6 h-6 text-sonarges-gray-600 dark:text-sonarges-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
              </div>
              <h3 className="font-bold text-sonarges-gray-900 dark:text-white mb-2">Actions en cours</h3>
              <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400 text-sm">
                Gérez vos tâches et actions assignées
              </p>
            </div>

            <div className="bg-gradient-to-br from-sonarges-red-50 to-white dark:from-sonarges-red-900/20 dark:to-sonarges-gray-800 p-6 rounded-xl shadow-lg border border-sonarges-red-200 dark:border-sonarges-red-800 hover:shadow-xl transition-all duration-200">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-sonarges-red-100 dark:bg-sonarges-red-900/30 rounded-lg">
                  <svg className="w-6 h-6 text-sonarges-red-600 dark:text-sonarges-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <h3 className="font-bold text-sonarges-gray-900 dark:text-white mb-2">Validations en attente</h3>
              <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400 text-sm">
                Documents nécessitant votre validation
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
